if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    /**
     * Main form submission handler with cart clearing logic
     * @param {Event} evt - Form submit event
     */
    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      this.setButtonState('loading');

      try {
        const clearCartModalId = this.dataset?.clearCartModalId;
        
        // If no modal or no subscription items, proceed directly
        if (!clearCartModalId || !(await this.checkCartForSubscriptions())) {
          await this.proceedWithAddToCart();
          return;
        }

        // Show clear cart modal
        this.showClearCartModal(clearCartModalId);
        
      } catch (error) {
        console.error('Error in form submission:', error);
        this.handleErrorMessage('Unable to process request. Please try again.');
        this.setButtonState('normal');
      }
    }

    /**
     * Checks cart for subscription items (LPS products)
     * @returns {Promise<boolean>} True if cart contains subscription items
     */
    async checkCartForSubscriptions() {
      try {
        const response = await fetch(`${routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }
        
        const cart = await response.json();
        return Array.isArray(cart.items) && cart.items.some(item => item?.product_type === 'LPS');
      } catch (error) {
        console.error('Error checking cart:', error);
        return false; // Proceed with add to cart if check fails
      }
    }

    /**
     * Shows the clear cart confirmation modal
     * @param {string} modalId - Modal element ID
     */
    showClearCartModal(modalId) {
      const modal = document.getElementById(modalId);
      if (!modal) {
        console.error('Clear cart modal not found:', modalId);
        this.setButtonState('normal');
        return;
      }

      this.setButtonState('normal');

      // Get modal buttons
      const cancelBtn = modal.querySelector('[id^="ClearCartCancel-"]');
      const confirmBtn = modal.querySelector('[id^="ClearCartConfirm-"]');
      const closeBtn = modal.querySelector('[id^="ModalClose-"]');

      const handleCancel = () => {
        this.toggleModal(modal, false);
        this.cleanupModalHandlers(modal, cancelBtn, confirmBtn, closeBtn);
      };

      const handleConfirm = async () => {
        try {
          await fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' });
          this.toggleModal(modal, false);
          this.cleanupModalHandlers(modal, cancelBtn, confirmBtn, closeBtn);
          await this.proceedWithAddToCart();
        } catch (error) {
          console.error('Error clearing cart:', error);
          this.handleErrorMessage('Failed to clear cart. Please try again.');
          this.setButtonState('normal');
        }
      };

      // Add event listeners
      cancelBtn?.addEventListener('click', handleCancel);
      closeBtn?.addEventListener('click', handleCancel);
      confirmBtn?.addEventListener('click', handleConfirm);

      // Store handlers for cleanup
      modal._handlers = { cancel: handleCancel, confirm: handleConfirm };

      // Show modal
      this.toggleModal(modal, true);
    }

    /**
     * Toggles modal visibility (show/hide)
     * @param {HTMLElement} modal - Modal element
     * @param {boolean} show - Whether to show or hide modal
     */
    toggleModal(modal, show) {
      if (modal.show && modal.hide) {
        show ? modal.show(this.submitButton) : modal.hide();
      } else {
        if (show) {
          modal.removeAttribute('hidden');
          modal.setAttribute('open', '');
          document.body.classList.add('overflow-hidden');
        } else {
          modal.removeAttribute('open');
          modal.setAttribute('hidden', 'hidden');
          document.body.classList.remove('overflow-hidden');
        }
      }
    }

    /**
     * Cleans up modal event handlers
     * @param {HTMLElement} modal - Modal element
     * @param {HTMLElement} cancelBtn - Cancel button element
     * @param {HTMLElement} confirmBtn - Confirm button element
     * @param {HTMLElement} closeBtn - Close button element
     */
    cleanupModalHandlers(modal, cancelBtn, confirmBtn, closeBtn) {
      if (modal._handlers) {
        cancelBtn?.removeEventListener('click', modal._handlers.cancel);
        closeBtn?.removeEventListener('click', modal._handlers.cancel);
        confirmBtn?.removeEventListener('click', modal._handlers.confirm);
        delete modal._handlers;
      }
    }

    /**
     * Sets button state (loading, disabled, normal)
     * @param {string} state - Button state
     */
    setButtonState(state) {
      const spinner = this.querySelector('.loading-overlay__spinner');
      
      switch (state) {
        case 'loading':
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('loading');
          spinner?.classList.remove('hidden');
          break;
        case 'disabled':
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.remove('loading');
          spinner?.classList.add('hidden');
          break;
        case 'normal':
        default:
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          spinner?.classList.add('hidden');
          break;
      }
    }

    /**
     * Proceeds with the actual add to cart operation
     */
    async proceedWithAddToCart() {
      this.setButtonState('loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        
        if (!response.ok) {
          const errorData = await response.json();
          this.handleErrorMessage(errorData.description || errorData.message || 'Failed to add item to cart');
          this.setButtonState('disabled');
          return;
        }
        
        const data = await response.json();

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        this.updateCartUI(data);

      } catch (error) {
        console.error('Add to cart error:', error);
        this.handleErrorMessage('Failed to add item to cart. Please try again.');
      } finally {
        this.setButtonState('normal');
        if (this.cart && this.cart.classList.contains('is-empty')) {
          this.cart.classList.remove('is-empty');
        }
      }
    }

    /**
     * Updates cart UI after successful add to cart
     * @param {Object} data - Cart response data
     */
    updateCartUI(data) {
      if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form'});
      this.error = false;

      const quickAddModal = this.closest('quick-add-modal');
      if (quickAddModal) {
        document.body.addEventListener('modalClosed', () => {
          setTimeout(() => { this.cart.renderContents(data) });
        }, { once: true });
        quickAddModal.hide(true);
      } else {
        this.cart.renderContents(data);
        this.trackAddToCartEvent(data);
      }
    }

    /**
     * Tracks add to cart event for analytics
     * @param {Object} data - Cart response data
     */
    trackAddToCartEvent(data) {
      if (!window.dataLayer) return;

      try {
        window.dataLayer.push({
          event: 'add_to_cart',
          currency: 'USD',
          value: data.quantity * data.final_price,
          ecommerce: {
            items: [{
              item_name: data.product_title,
              item_id: data.product_id,
              item_brand: 'moen',
              item_category: '',
              item_category2: '',
              item_list_name: 'product Listing',
              item_variant: data.variant_id,
              coupon: '',
              discount: '',
              quantity: data.quantity,
              price: data.final_price,
            }],
          },
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
    }


    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
